import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AppSettingsService } from '@services/app-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { SHARED_MODULES, SHARED_TUAIN_FORMS } from '@shared/shared-standalone';
import { MenuLeftComponent } from '@layout/common/menu/menu-left/menu-left.component';
import { FooterComponent } from '@layout/common/footer/footer.component';
import { AccessibilityWidgetComponent } from '@layout/common/accessibility-widget/accessibility-widget.component';

import { AppFrontendConfigComponent } from '@features/control/fontend-config/frontend-config.component';
import { slideFadeinUp, slideFadeinRight, zoomFadein, fadein } from '@layout/common/router-animations';
import { EventManagerService } from '@services/event-manager.service';
import { AppConfigService } from '@services/app-configuration.service';
import { defaultConfig } from '../../app.config';

@Component({
  standalone: true,
  selector: 'layout-main',
  imports: [
    ...SHARED_MODULES,
    SHARED_TUAIN_FORMS,
    AppFrontendConfigComponent,
    MenuLeftComponent,
    CdkScrollable,
    FooterComponent,
    AccessibilityWidgetComponent,
  ],
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideFadeinUp, slideFadeinRight, zoomFadein, fadein],
})
export class LayoutMainComponent implements OnInit {
  private readonly _cdr = inject(ChangeDetectorRef);
  private settings = inject(AppSettingsService);
  private _eventManager = inject(EventManagerService);
  private _appConfig = inject(AppConfigService);

  private destroyRef = inject(DestroyRef);
  assetsBase: string;
  isContentMaxWidth: boolean;
  isAppMaxWidth: boolean;
  isGrayBackground: boolean;
  isSquaredBorders: boolean;
  isCardShadow: boolean;
  isBorderless: boolean;
  menuLayoutType: string;
  isMobileView: boolean;
  isMobileMenuOpen: boolean;
  routerAnimation: string;
  isMenuCollapsed: boolean;
  leftMenuWidth: number;
  isTopbarFixed: boolean;
  isGrayTopbar: boolean;

  touchStartPrev = 0;
  touchStartLocked = false;

  constructor() {
    const { configLoaded } = defaultConfig.appEvents;
    this._eventManager
      .getSubject(configLoaded.name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.assetsBase = this._appConfig.getParameter('assetsBase')));
    this.settings.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(state => {
      this.isContentMaxWidth = state.isContentMaxWidth;
      this.isAppMaxWidth = state.isAppMaxWidth;
      this.isGrayBackground = state.isGrayBackground;
      this.isSquaredBorders = state.isSquaredBorders;
      this.isCardShadow = state.isCardShadow;
      this.isBorderless = state.isBorderless;
      this.menuLayoutType = state.menuLayoutType;
      this.isMobileView = state.isMobileView;
      this.isMobileMenuOpen = state.isMobileMenuOpen;
      this.routerAnimation = state.routerAnimation;
      this.isMenuCollapsed = state.isMenuCollapsed;
      this.leftMenuWidth = state.leftMenuWidth;
      this.isTopbarFixed = state.isTopbarFixed;
      this.isGrayTopbar = state.isGrayTopbar;
      this._cdr.markForCheck();
    });
  }

  ngOnInit() {
    this.bindMobileSlide();
  }

  onCollapse(value: any) {
    this.settings.set({ isMenuCollapsed: value });
  }

  toggleCollapsed() {
    this.settings.set({ isMenuCollapsed: !this.isMenuCollapsed });
  }

  toggleMobileMenu() {
    this.settings.set({ isMobileMenuOpen: !this.isMobileMenuOpen });
  }

  openMobileMenu() {
    this.settings.set({ isMobileMenuOpen: true });
  }

  closeMobileMenu() {
    this.settings.set({ isMobileMenuOpen: false });
  }

  private onTouchStart = (e: TouchEvent) => {
    const x = (e.changedTouches ? e.changedTouches[0] : (e as any)).clientX;
    this.touchStartPrev = x;
    this.touchStartLocked = x > 70;
  };

  private onTouchMove = (e: TouchEvent) => {
    if (!this.isMobileView) {
      return;
    }
    const x = (e.changedTouches ? e.changedTouches[0] : (e as any)).clientX;
    const prev = this.touchStartPrev;
    if (x - prev > 50 && !this.touchStartLocked) {
      this.openMobileMenu();
      this.touchStartLocked = true;
    }
  };

  bindMobileSlide() {
    document.addEventListener('touchstart', this.onTouchStart, { passive: false });
    document.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('touchstart', this.onTouchStart);
      document.removeEventListener('touchmove', this.onTouchMove);
    });
  }

  routeAnimation(outlet: RouterOutlet, animation: string) {
    if (animation === this.routerAnimation) {
      return outlet.isActivated && outlet.activatedRoute.routeConfig.path;
    }
  }
}
